import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { ProvidersService } from '../../../orval-gen/endpoints/providers/providers.service';
import { AgencyProvidersList200DataItem as ProviderRow } from '../../../orval-gen/models';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { LocalStorageService } from '../../shared/services/local-storage.service';
import { unwrap } from '../../utils/http-utils';

type StatusFilter = 'all' | 'active' | 'inactive';

@Component({
  selector: 'app-providers',
  imports: [FormsModule, IconComponent, ConfirmDialogComponent],
  templateUrl: './providers.component.html',
  styleUrl: './providers.component.scss',
})
export class ProvidersComponent implements OnInit {
  private readonly providersService = inject(ProvidersService);
  private readonly toastr = inject(ToastrService);
  private readonly ls = inject(LocalStorageService);

  allRows: ProviderRow[] = [];
  loaded = false;
  saving = false;
  query = '';
  statusFilter: StatusFilter = 'all';
  sortDir: 'asc' | 'desc' = 'asc';
  /** Provider waiting for the "Deactivate?" confirmation. */
  pendingDeactivation: ProviderRow | null = null;

  /** The switch is the consumer's, so only managers of its super agency may throw it. */
  readonly canEdit = this.ls.getBool('isConsumerOwner');

  readonly filters: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'active', label: 'Active' },
    { key: 'inactive', label: 'Inactive' },
  ];

  ngOnInit(): void {
    this.getProviders();
  }

  getProviders(): void {
    this.loaded = false;
    this.providersService
      .agencyProvidersList()
      .pipe(
        unwrap(),
        finalize(() => (this.loaded = true))
      )
      .subscribe({
        next: rows => (this.allRows = rows ?? []),
        error: error => console.error('Failed to load providers:', error),
      });
  }

  /** Whether agents see the provider: switched on by the agency and not switched off by AirGateway. */
  isLive(row: ProviderRow): boolean {
    return row.enabled && row.active;
  }

  get rows(): ProviderRow[] {
    const q = this.query.trim().toLowerCase();
    return this.allRows
      .filter(row => {
        if (this.statusFilter === 'active' && !this.isLive(row)) return false;
        if (this.statusFilter === 'inactive' && this.isLive(row)) return false;
        if (!q) return true;
        return (
          row.provider.agw_id.toLowerCase().includes(q) ||
          (row.provider.name ?? '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => {
        const cmp = (a.provider.name || a.provider.agw_id).localeCompare(
          b.provider.name || b.provider.agw_id
        );
        return this.sortDir === 'asc' ? cmp : -cmp;
      });
  }

  count(filter: StatusFilter): number {
    const rows = this.allRows;
    if (filter === 'active') return rows.filter(r => this.isLive(r)).length;
    if (filter === 'inactive') return rows.filter(r => !this.isLive(r)).length;
    return rows.length;
  }

  toggleSort(): void {
    this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
  }

  /** Activation applies straight away; deactivation asks first. */
  onToggle(row: ProviderRow): void {
    if (row.enabled) {
      this.pendingDeactivation = row;
    } else {
      this.toggleProvider(row);
    }
  }

  confirmDeactivation(): void {
    if (!this.pendingDeactivation) return;
    this.toggleProvider(this.pendingDeactivation);
    this.pendingDeactivation = null;
  }

  toggleProvider(row: ProviderRow): void {
    const enabled = !row.enabled;
    const replace = (updated: ProviderRow) =>
      (this.allRows = this.allRows.map(r => (r.provider.id === row.provider.id ? updated : r)));
    replace({ ...row, enabled });
    this.saving = true;

    this.providersService
      .agencyProvidersUpdate(row.provider.id, { enabled })
      .pipe(
        unwrap(),
        finalize(() => (this.saving = false))
      )
      .subscribe({
        next: updated => {
          replace(updated);
          this.toastr.success(
            `${updated.provider.name || updated.provider.agw_id} ${enabled ? 'enabled' : 'disabled'}.`,
            'Success'
          );
        },
        error: err => {
          console.error('Failed to update provider:', err);
          replace(row);
        },
      });
  }
}
